import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  MessageSquare,
  CheckSquare,
  User,
  LogOut,
  Users,
  BookOpen,
  Newspaper,
  Rss,
  Calendar,
  FolderOpen
} from 'lucide-react'
import { useAuth } from '@/contexts/Auth0Context'
import { UserRole } from '@/types/user'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import NotificationBell from '@/components/notifications/NotificationBell'

const Navigation: React.FC = () => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const dashboardRoute = user?.role === UserRole.MENTOR ? '/mentor/dashboard' : '/mentee/dashboard'

  const navigationItems = [
    {
      name: 'Dashboard',
      href: dashboardRoute,
      icon: Home
    },
    {
      name: 'Messages',
      href: '/messages',
      icon: MessageSquare
    },
    {
      name: 'Tasks',
      href: '/tasks',
      icon: CheckSquare
    },
    {
      name: 'Meetings',
      href: '/meetings',
      icon: Calendar
    },
    {
      name: 'Resources',
      href: '/resources',
      icon: FolderOpen
    },
    {
      name: 'News',
      href: '/news',
      icon: Rss
    }
  ]

  if (import.meta.env.VITE_ENABLE_FEED === 'true') {
    navigationItems.push({
      name: 'Feed',
      href: '/feed',
      icon: Newspaper
    })
  }

  // Add mentor-specific items
  if (user?.role === UserRole.MENTOR) {
    navigationItems.push({
      name: 'Mentees',
      href: '/mentees',
      icon: Users
    })
  }

  // Add mentee-specific items
  if (user?.role === UserRole.MENTEE) {
    navigationItems.push({
      name: 'Find Mentors',
      href: '/find-mentors',
      icon: BookOpen
    })
  }

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and navigation */}
          <div className="flex items-center">
            <Link to={dashboardRoute} className="flex-shrink-0 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M4 18 L10 6 L14 14 L20 4" stroke="#1D4ED8" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="text-xl font-display text-gray-900">SkyMent</h1>
            </Link>

            {/* Navigation links */}
            <div className="hidden sm:ml-8 sm:flex sm:items-center sm:space-x-1">
              {navigationItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname.startsWith(item.href)

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      inline-flex items-center px-3 py-2 rounded-lg text-sm font-bold transition-colors
                      ${isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.name}
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <NotificationBell />

            {/* User info */}
            <div className="hidden md:flex md:items-center md:space-x-3 flex-shrink-0">
              <div className="text-sm text-right">
                <div className="font-bold text-gray-900">
                  {user?.firstName} {user?.lastName}
                </div>
                <Badge variant={user?.role === UserRole.MENTOR ? 'mentor' : 'mentee'} className="capitalize">
                  {user?.role}
                </Badge>
              </div>

              {/* Profile link */}
              <Link
                to="/profile"
                className={`
                  p-0.5 rounded-full transition-colors flex-shrink-0
                  ${location.pathname === '/profile'
                    ? 'ring-2 ring-primary-500'
                    : 'hover:ring-2 hover:ring-gray-300'
                  }
                `}
              >
                <Avatar
                  src={user?.profile?.profilePicture}
                  name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                  size="xs"
                />
              </Link>
            </div>

            {/* Logout button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 ml-2"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="sm:hidden">
        <div className="pt-2 pb-3 space-y-1 bg-gray-50 border-t border-gray-200">
          {navigationItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname.startsWith(item.href)
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center px-4 py-2 text-sm font-bold transition-colors
                  ${isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                <Icon className="w-4 h-4 mr-3" />
                {item.name}
              </Link>
            )
          })}

          {/* Mobile profile link */}
          <Link
            to="/profile"
            className={`
              flex items-center px-4 py-2 text-sm font-bold transition-colors
              ${location.pathname === '/profile'
                ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }
            `}
          >
            <User className="w-4 h-4 mr-3" />
            Profile
          </Link>
        </div>
      </div>
    </nav>
  )
}

export default Navigation